import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUserPool, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import './Signup.css';
import logoImage from '../../assets/icon_logo.png';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    givenName: '',
    familyName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const passwordRequirements = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[^A-Za-z0-9]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!isPasswordValid) {
      setError('Password does not meet all requirements');
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: formData.email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: formData.givenName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: formData.familyName }),
    ];

    userPool.signUp(
      formData.email,
      formData.password,
      attributeList,
      null,
      (err, result) => {
        if (err) {
          console.error('Signup failed:', err);
          setError(err.message || 'Signup failed');
          setLoading(false);
          return;
        }

        console.log('Signup successful, user needs to verify email');
        // Navigate to verification page with email
        navigate('/verify-email', { state: { email: formData.email } });
      }
    );
  };

  return (
    <div className="auth-container">
      <div className="auth-card signup-card">
        <div className="auth-header">
          <img src={logoImage} alt="CoinedIn Logo" className="auth-logo" />
          <h1>CoinedIn</h1>
          <p className="auth-tagline">Recognize and Celebrate Success</p>
        </div>

        <form onSubmit={handleSignup} className="auth-form">
          <h2>Create Account</h2>

          {error && (
            <div className="auth-error">
              <span className="error-icon">⚠</span>
              <p>{error}</p>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="givenName">First Name</label>
              <input
                id="givenName"
                name="givenName"
                type="text"
                value={formData.givenName}
                onChange={handleChange}
                placeholder="John"
                required
                disabled={loading}
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="familyName">Last Name</label>
              <input
                id="familyName"
                name="familyName"
                type="text"
                value={formData.familyName}
                onChange={handleChange}
                placeholder="Doe"
                required
                disabled={loading}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a strong password"
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {formData.password && (
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                <li className={passwordRequirements.length ? 'met' : ''}>
                  At least 8 characters
                </li>
                <li className={passwordRequirements.uppercase ? 'met' : ''}>
                  One uppercase letter
                </li>
                <li className={passwordRequirements.lowercase ? 'met' : ''}>
                  One lowercase letter
                </li>
                <li className={passwordRequirements.number ? 'met' : ''}>
                  One number
                </li>
                <li className={passwordRequirements.special ? 'met' : ''}>
                  One special character
                </li>
              </ul>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              required
              disabled={loading}
              autoComplete="new-password"
            />
            {formData.confirmPassword && !passwordsMatch && (
              <small className="error-text">Passwords do not match</small>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || !isPasswordValid || !passwordsMatch}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <div className="auth-links">
            <span>Already have an account?</span>
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Signup;
